import { useQuery } from '@tanstack/react-query';
import {
  fetchIssueStatusDistribution,
  fetchCreatedVsResolved,
  fetchPriorityDistribution,
  fetchAssigneeWorkload,
  fetchSprintBurndown,
  fetchTestExecutionSummary,
  fetchRequirementCoverage,
  fetchActivities,
} from '../services/dashboardService';

const STALE_TIME = 5 * 60 * 1000; // 5분 (명세 3.7.4)

export const dashboardKeys = {
  all: (pid: string) => ['dashboard', pid] as const,
  statusDist: (pid: string) => ['dashboard', pid, 'status-dist'] as const,
  createdVsResolved: (pid: string, period: string, from?: string, to?: string) => ['dashboard', pid, 'created-vs-resolved', period, from, to] as const,
  priorityDist: (pid: string) => ['dashboard', pid, 'priority-dist'] as const,
  assigneeWorkload: (pid: string) => ['dashboard', pid, 'assignee-workload'] as const,
  burndown: (pid: string) => ['dashboard', pid, 'burndown'] as const,
  testExecution: (pid: string) => ['dashboard', pid, 'test-execution'] as const,
  reqCoverage: (pid: string) => ['dashboard', pid, 'req-coverage'] as const,
  activities: (pid: string) => ['dashboard', pid, 'activities'] as const,
};

export function useIssueStatusDistribution(projectId: string) {
  return useQuery({
    queryKey: dashboardKeys.statusDist(projectId),
    queryFn: () => fetchIssueStatusDistribution(projectId),
    enabled: !!projectId,
    staleTime: STALE_TIME,
  });
}

export function useCreatedVsResolved(projectId: string, period: string, from?: string, to?: string) {
  return useQuery({
    queryKey: dashboardKeys.createdVsResolved(projectId, period, from, to),
    queryFn: () => fetchCreatedVsResolved(projectId, { period, from, to }),
    enabled: !!projectId,
    staleTime: STALE_TIME,
  });
}

export function usePriorityDistribution(projectId: string) {
  return useQuery({
    queryKey: dashboardKeys.priorityDist(projectId),
    queryFn: () => fetchPriorityDistribution(projectId),
    enabled: !!projectId,
    staleTime: STALE_TIME,
  });
}

export function useAssigneeWorkload(projectId: string) {
  return useQuery({
    queryKey: dashboardKeys.assigneeWorkload(projectId),
    queryFn: () => fetchAssigneeWorkload(projectId),
    enabled: !!projectId,
    staleTime: STALE_TIME,
  });
}

export function useSprintBurndown(projectId: string) {
  return useQuery({
    queryKey: dashboardKeys.burndown(projectId),
    queryFn: () => fetchSprintBurndown(projectId),
    enabled: !!projectId,
    staleTime: STALE_TIME,
  });
}

export function useTestExecutionSummary(projectId: string) {
  return useQuery({
    queryKey: dashboardKeys.testExecution(projectId),
    queryFn: () => fetchTestExecutionSummary(projectId),
    enabled: !!projectId,
    staleTime: STALE_TIME,
  });
}

export function useRequirementCoverage(projectId: string) {
  return useQuery({
    queryKey: dashboardKeys.reqCoverage(projectId),
    queryFn: () => fetchRequirementCoverage(projectId),
    enabled: !!projectId,
    staleTime: STALE_TIME,
  });
}

export function useActivities(projectId: string, limit = 20) {
  return useQuery({
    queryKey: dashboardKeys.activities(projectId),
    queryFn: () => fetchActivities(projectId, limit),
    enabled: !!projectId,
    staleTime: 60_000, // 활동은 1분 (실시간 보완)
  });
}
